/**
 * The Briefs page's Email control (Foundry Intelligence, build order step 5; GET and PUT /v1/twin/briefs/email): who
 * gets each cadence's new briefs by email. A button in the page's header opens a panel with a box for each role under
 * each cadence. An org admin ticks and saves; every other member sees the same boxes, read-only. While the platform's
 * own switch is off the button says "off" and the panel says that saving only chooses who will get each brief once it
 * is on. The control is shown only where the page can read the choice: the live platform, serving the route.
 *
 * BriefEmailPanel is pure (rendered in tests without providers); BriefEmailControl holds the read, the draft and the
 * save. Every word is lib/brief/email's.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail, Save } from "lucide-react";
import {
  BRIEF_EMAIL_KEY,
  saveBriefEmail,
  useBriefEmailQuery,
  type BriefEmailCadence,
  type BriefEmailCadences,
  type BriefEmailRole,
  type BriefEmailSettings,
} from "@/lib/api/brief";
import { errorDetailOf } from "@/lib/api/error";
import {
  EMAIL_CADENCES,
  EMAIL_OWNER_NOTE,
  EMAIL_ROLES,
  EMAIL_SAVED,
  EMAIL_TITLE,
  emailBoxLabel,
  emailLead,
  emailPlatformNote,
  emailSaveError,
  sameEmailChoice,
  withEmailRole,
} from "@/lib/brief/email";
import { useOrg } from "@/lib/org-context";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { cn } from "@/lib/ui/cn";
import { Sheet } from "@/lib/ui/sheet";

export interface BriefEmailPanelProps {
  /** The choice as saved, and the platform's switch. */
  settings: BriefEmailSettings;
  /** The roles ticked now: the admin's edit, or the saved choice. */
  draft: BriefEmailCadences;
  /** org.admin: the boxes and Save work; for anyone else they are read-only. */
  canEdit: boolean;
  saving?: boolean;
  saved?: boolean;
  error?: string | null;
  onToggle?: (cadence: BriefEmailCadence, role: BriefEmailRole, on: boolean) => void;
  onSave?: () => void;
  onClose?: () => void;
}

/** The choice as a table: a row per cadence, a box per role, and Save for an org admin once the choice changed. */
export function BriefEmailPanel({
  settings,
  draft,
  canEdit,
  saving = false,
  saved = false,
  error = null,
  onToggle,
  onSave,
  onClose,
}: BriefEmailPanelProps) {
  const changed = !sameEmailChoice(draft, settings.cadences);
  return (
    <div className="flex flex-col gap-4 p-5">
      <div className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-base font-semibold text-on-surface">
          <Mail className="size-4 text-on-surface-muted" aria-hidden />
          {EMAIL_TITLE}
        </h2>
        <p className="text-sm text-on-surface-muted">{emailLead(canEdit)}</p>
      </div>
      <p
        className={cn(
          "rounded-md border p-3 text-sm text-on-surface",
          settings.platform_enabled ? "border-border" : "border-warning/40 bg-warning/5",
        )}
      >
        {emailPlatformNote({ platformEnabled: settings.platform_enabled, canEdit })}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[30rem] border-collapse text-sm">
          <caption className="sr-only">The roles each cadence&apos;s new briefs are emailed to</caption>
          <thead>
            <tr>
              {/* The cadence column stays put while a phone scrolls the roles sideways. */}
              <th scope="col" className="sticky left-0 bg-surface py-2 pr-3 text-left text-xs font-medium text-on-surface-muted">
                Brief
              </th>
              {EMAIL_ROLES.map((r) => (
                <th key={r.id} scope="col" className="px-1.5 py-2 text-center text-xs font-medium text-on-surface-muted">
                  {r.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EMAIL_CADENCES.map((c) => (
              <tr key={c.id} className="border-t border-border">
                <th scope="row" className="sticky left-0 bg-surface py-2 pr-3 text-left font-medium text-on-surface">
                  {c.label}
                </th>
                {EMAIL_ROLES.map((r) => (
                  <td key={r.id} className="px-1.5 py-2 text-center">
                    <input
                      type="checkbox"
                      className="size-4 accent-brand disabled:cursor-not-allowed"
                      aria-label={emailBoxLabel(c.id, r.id)}
                      checked={draft[c.id].includes(r.id)}
                      disabled={!canEdit || saving}
                      onChange={(e) => onToggle?.(c.id, r.id, e.target.checked)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-on-surface-muted">{EMAIL_OWNER_NOTE}</p>
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        {saved && !changed && !error && (
          <span role="status" className="text-sm text-on-surface-muted">
            {EMAIL_SAVED}
          </span>
        )}
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
        {canEdit && (
          <Button type="button" size="sm" onClick={onSave} disabled={!changed || saving}>
            {saving ? <Loader2 className="animate-spin" aria-hidden /> : <Save aria-hidden />}
            Save
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * The header's Email button and its panel. Nothing where the page cannot read the choice: the sample plant (no live
 * API), a platform that does not serve the route yet, or a read still on its way or failed.
 */
export function BriefEmailControl() {
  const org = useOrg();
  const read = useBriefEmailQuery();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<BriefEmailCadences | null>(null);
  const save = useMutation({
    mutationFn: saveBriefEmail,
    onSuccess: (stored) => {
      queryClient.setQueryData(BRIEF_EMAIL_KEY, stored);
      setDraft(null);
    },
  });
  const settings = read.data;
  if (!settings) return null;
  // The org's permission is asked only once there is a choice to show (the sample plant never reads one).
  const canEdit = org.can("org.admin");
  const shown = draft ?? settings.cadences;
  const close = () => {
    setOpen(false);
    setDraft(null);
    save.reset();
  };
  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Mail aria-hidden />
        Email
        {!settings.platform_enabled && <Badge variant="secondary">off</Badge>}
      </Button>
      <Sheet open={open} onClose={close} widthClass="max-w-2xl" ariaLabel={EMAIL_TITLE}>
        <BriefEmailPanel
          settings={settings}
          draft={shown}
          canEdit={canEdit}
          saving={save.isPending}
          saved={save.isSuccess}
          error={save.isError ? emailSaveError(errorDetailOf(save.error)) : null}
          onToggle={(cadence, role, on) => {
            save.reset();
            setDraft(withEmailRole(shown, cadence, role, on));
          }}
          onSave={() => save.mutate(shown)}
          onClose={close}
        />
      </Sheet>
    </>
  );
}

/**
 * Who gets which brief, inline: the Email tab of Settings (the 2026-10-06 next-phase plan, step 3), the same panel the
 * brief page opens in a sheet. Nothing where there is no choice to show (no live API, a platform that does not serve
 * the route yet, or a read still on its way or failed).
 */
export function BriefEmailSettings() {
  const org = useOrg();
  const read = useBriefEmailQuery();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<BriefEmailCadences | null>(null);
  const save = useMutation({
    mutationFn: saveBriefEmail,
    onSuccess: (stored) => {
      queryClient.setQueryData(BRIEF_EMAIL_KEY, stored);
      setDraft(null);
    },
  });
  const settings = read.data;
  if (!settings) return null;
  const shown = draft ?? settings.cadences;
  return (
    <BriefEmailPanel
      settings={settings}
      draft={shown}
      canEdit={org.can("org.admin")}
      saving={save.isPending}
      saved={save.isSuccess}
      error={save.isError ? emailSaveError(errorDetailOf(save.error)) : null}
      onToggle={(cadence, role, on) => {
        save.reset();
        setDraft(withEmailRole(shown, cadence, role, on));
      }}
      onSave={() => save.mutate(shown)}
    />
  );
}
